import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ProjectTemplate, Project, ProjectTask } from "../types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskInsertionManager } from "./TaskInsertionManager";
import { calculateSequentialTaskDates, calculateEndDate } from "../utils/dateUtils";
import { parse, format as fnsFormat } from "date-fns";

// Convert "dd MMM yyyy" to "yyyy-MM-dd" for <input type="date">
const toInputDateFormat = (dateStr: string): string => {
  if (!dateStr) return '';
  // Already in yyyy-MM-dd
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  // Convert from dd MMM yyyy
  if (dateStr.match(/^\d{2} [A-Za-z]{3} \d{4}$/)) {
    try {
      const parsed = parse(dateStr, 'dd MMM yyyy', new Date());
      return fnsFormat(parsed, 'yyyy-MM-dd');
    } catch { return ''; }
  }
  return dateStr;
};
interface ProjectDialogsProps {
  isNewProjectDialogOpen: boolean;
  setIsNewProjectDialogOpen: (isOpen: boolean) => void;
  isEditProjectDialogOpen: boolean;
  setIsEditProjectDialogOpen: (isOpen: boolean) => void;
  isDeleteProjectDialogOpen: boolean;
  setIsDeleteProjectDialogOpen: (isOpen: boolean) => void;
  isImportAlertOpen: boolean;
  setIsImportAlertOpen: (isOpen: boolean) => void;
  isImportDialogOpen: boolean;
  setIsImportDialogOpen: (isOpen: boolean) => void;
  selectedProject: Project | null;
  newProject: {
    name: string;
    description: string;
    projectLead: string;
    startDate: string;
    endDate: string;
    type: string;
    duration: number;
    tasks: ProjectTask[];
  };
  setNewProject: React.Dispatch<React.SetStateAction<{
    name: string;
    description: string;
    projectLead: string;
    startDate: string;
    endDate: string;
    type: string;
    duration: number;
    tasks: ProjectTask[];
  }>>;
  handleCreateProject: (e: React.FormEvent) => void;
  confirmEditProject: (e: React.FormEvent) => void;
  confirmDeleteProject: () => void;
  handleImportTemplate: () => void;
  templates: ProjectTemplate[];
  handleTaskDurationChange: (index: number, increase: boolean) => void;
  excludeWeekends: boolean;
  setExcludeWeekends: (checked: boolean) => void;
}
export const ProjectDialogs = ({
  isNewProjectDialogOpen,
  setIsNewProjectDialogOpen,
  isEditProjectDialogOpen,
  setIsEditProjectDialogOpen,
  isDeleteProjectDialogOpen,
  setIsDeleteProjectDialogOpen,
  isImportAlertOpen,
  setIsImportAlertOpen,
  isImportDialogOpen,
  setIsImportDialogOpen,
  selectedProject,
  newProject,
  setNewProject,
  handleCreateProject,
  confirmEditProject,
  confirmDeleteProject,
  handleImportTemplate,
  templates,
  handleTaskDurationChange,
  excludeWeekends,
  setExcludeWeekends
}: ProjectDialogsProps) => {
  const recalculateProjectEndDate = (tasks: ProjectTask[], startDate: string, excWeekends: boolean) => {
    if (!tasks.length || !startDate) return;

    const { startDates, endDates } = calculateSequentialTaskDates(
      startDate,
      tasks.map(task => ({ duration: task.duration })),
      excWeekends
    );

    const updatedTasks = tasks.map((task, index) => ({
      ...task,
      startDate: startDates[index] || task.startDate || '',
      endDate: endDates[index] || task.endDate || ''
    }));

    const lastEndDate = updatedTasks.length > 0 && updatedTasks[updatedTasks.length - 1].endDate
      ? updatedTasks[updatedTasks.length - 1].endDate
      : '';

    setNewProject(prev => ({
      ...prev,
      tasks: updatedTasks,
      duration: updatedTasks.reduce((sum, task) => sum + task.duration, 0),
      endDate: toInputDateFormat(lastEndDate)
    }));
  };

  // Recalculate all task dates and project end date when excludeWeekends or project start date changes
  useEffect(() => {
    if (isEditProjectDialogOpen && newProject.tasks.length > 0 && newProject.startDate) {
      recalculateProjectEndDate(newProject.tasks, newProject.startDate, excludeWeekends);
    }
  }, [excludeWeekends, newProject.startDate]);

  const handleTasksUpdate = (updatedTasks: ProjectTask[]) => {
    // Recalculate dates for updated tasks
    const { startDates, endDates } = calculateSequentialTaskDates(
      newProject.startDate,
      updatedTasks.map(task => ({ duration: task.duration })),
      excludeWeekends
    );

    const recalculatedTasks = updatedTasks.map((task, index) => ({
      ...task,
      startDate: startDates[index] || task.startDate || '',
      endDate: endDates[index] || task.endDate || ''
    }));

    const projectEndDate = recalculatedTasks.length > 0 && recalculatedTasks[recalculatedTasks.length - 1].endDate
      ? recalculatedTasks[recalculatedTasks.length - 1].endDate
      : '';

    setNewProject(prev => ({
      ...prev,
      tasks: recalculatedTasks,
      duration: recalculatedTasks.reduce((sum, task) => sum + task.duration, 0),
      endDate: toInputDateFormat(projectEndDate)
    }));
  };
  return <>
      {/* New Project Dialog */}
      <Dialog open={isNewProjectDialogOpen} onOpenChange={setIsNewProjectDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Enter the details for your new project.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProject}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input id="name" value={newProject.name} onChange={e => setNewProject({
                ...newProject,
                name: e.target.value
              })} className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="description" className="text-right">
                  Description
                </Label>
                <Textarea id="description" value={newProject.description} onChange={e => setNewProject({
                ...newProject,
                description: e.target.value
              })} className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="projectLead" className="text-right">
                  Project Lead
                </Label>
                <Input id="projectLead" value={newProject.projectLead} onChange={e => setNewProject({
                ...newProject,
                projectLead: e.target.value
              })} className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="startDate" className="text-right">
                  Start Date
                </Label>
                <Input id="startDate" type="date" value={newProject.startDate} onChange={e => setNewProject({
                ...newProject,
                startDate: e.target.value
              })} className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="endDate" className="text-right">
                  End Date
                </Label>
                <Input id="endDate" type="date" value={newProject.endDate} onChange={e => setNewProject({
                ...newProject,
                endDate: e.target.value
              })} className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="type" className="text-right">
                  Template
                </Label>
                <Select value={newProject.type} onValueChange={value => setNewProject({
                ...newProject,
                type: value
              })}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {templates.map(template => <SelectItem key={template.id} value={template.title}>
                        {template.title}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsNewProjectDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={isEditProjectDialogOpen} onOpenChange={setIsEditProjectDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900">
          <DialogHeader className="py-4 text-2xl text-white">
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription className="py-0 h-[15px] text-gray-500">
              Update project details and manage tasks. You can add or delete project tasks using the "+" and "-" buttons, and change the task sequence using the up and down arrows.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={confirmEditProject}>
            <div className="flex flex-row gap-6">
              {/* Left column - Project details */}
              <div className="w-1/2 space-y-1 py-1">
                <div className="grid grid-cols-4 items-center gap-4 py-4">
                  <Label htmlFor="projectName" className="text-left text-white font-bold">
                    Project Name
                  </Label>
                  <Input id="projectName" value={newProject.name} onChange={e => setNewProject({
                  ...newProject,
                  name: e.target.value
                })} className="col-span-3 border-black" />
                </div>
                
                <div className="grid grid-cols-4 items-center gap-4 py-0">
                  <Label htmlFor="projectDescription" className="text-left text-white font-bold">
                    Description
                  </Label>
                  <Textarea id="projectDescription" value={newProject.description} onChange={e => setNewProject({
                  ...newProject,
                  description: e.target.value
                })} className="col-span-3 border-black" />
                </div>
                
                <div className="grid grid-cols-4 items-center gap-4 py-4">
                  <Label htmlFor="projectLead" className="text-left text-white font-bold">
                    Project Lead
                  </Label>
                  <Input id="projectLead" value={newProject.projectLead} onChange={e => setNewProject({
                  ...newProject,
                  projectLead: e.target.value
                })} className="col-span-3 border-black" />
                </div>
                
                {/* Dates */}
                <div className="flex space-x-0 pt-0 text-left">
                  <div className="flex-1 py-4">
                    <Label htmlFor="startDate" className="block mb-4 text-white font-bold">
                      Project Start Date
                    </Label>
                    <Input id="startDate" type="date" value={newProject.startDate} onChange={e => setNewProject({
                    ...newProject,
                    startDate: e.target.value
                  })} className="w-full border-black" />
                  </div>
                  
                  <div className="flex-1 py-4">
                    <Label htmlFor="endDate" className="block mb-4 text-white font-bold">Project End Date</Label>
                    <Input id="endDate" type="date" value={newProject.endDate} readOnly={newProject.tasks.length > 0} disabled={newProject.tasks.length > 0} className={`w-full text-white ${newProject.tasks.length > 0 ? 'bg-black' : ''}`} onChange={e => setNewProject({
                    ...newProject,
                    endDate: e.target.value
                  })} />
                  </div>
                  
                  <div className="flex-1 py-4">
                    <Label htmlFor="totalDuration" className="block mb-4 text-white font-bold">
                      Total Duration
                    </Label>
                    <Input id="totalDuration" type="text" value={`${newProject.tasks.reduce((sum, task) => sum + task.duration, 0)} days`} readOnly disabled className="w-full bg-black text-white" />
                  </div>
                </div>

                {/* Weekend Exclusion Checkbox */}
                <div className="flex items-center space-x-2 py-4">
                  <Checkbox id="excludeWeekendsEdit" checked={excludeWeekends} onCheckedChange={setExcludeWeekends} />
                  <Label htmlFor="excludeWeekendsEdit" className="text-sm text-white font-medium">
                    Exclude weekends when projecting date and duration
                  </Label>
                </div>
              </div>
              
              {/* Right column - Enhanced Tasks Management */}
              <div className="w-1/2 py-1">
                
                <div className="border rounded-lg p-4 h-[400px] overflow-y-auto border-black bg-secondary py-[4px] px-[8px] text-[12px]">
                  {newProject.tasks.length > 0 ? <TaskInsertionManager tasks={newProject.tasks} onTasksUpdate={handleTasksUpdate} excludeWeekends={excludeWeekends} projectStartDate={newProject.startDate} /> : <div className="p-4 text-center text-gray-400">
                      No tasks defined for this project
                    </div>}
                </div>
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditProjectDialogOpen(false)} className="rounded-xl border-black/50 bg-white text-black">
                Cancel
              </Button>
              <Button type="submit" className="rounded-xl">Update Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Project Dialog */}
      <AlertDialog open={isDeleteProjectDialogOpen} onOpenChange={setIsDeleteProjectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the project
              and all associated tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteProjectDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteProject}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Alert Dialog */}
      <AlertDialog open={isImportAlertOpen} onOpenChange={setIsImportAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import Project</AlertDialogTitle>
            <AlertDialogDescription>
              Feature to import projects coming soon.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsImportAlertOpen(false)}>OK</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog (placeholder for future implementation) */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Project</DialogTitle>
            <DialogDescription>
              Import a project from an existing template or file.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p>Import functionality will be available soon.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsImportDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
};